import React, { useState, useEffect } from 'react';
import { PostSessions } from '../../service/SessionAPI';
import { Toast } from './Toast';
import { encryptStorage } from '../../constant';

export const SessionForm = ({ onClose, sessionData, slots, fetchSessions }) => {
    // State variables for form fields
    const [sessionName, setSessionName] = useState('');
    const [quarter, setQuarter] = useState('');
    const [noOfSessions, setNoOfSessions] = useState('');
    const [sessions, setSessions] = useState([]); // To hold added sessions
    const [sessionDate, setSessionDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [registrationsAllowed, setRegistrationsAllowed] = useState('');
    const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
    const [sortedColumn, setSortedColumn] = useState(''); // Column currently being sorted
    const [deletedSlots, setDeletedSlots] = useState([]);
    const [grade, setGrade] = useState('');


    // State for editing
    const [editingIndex, setEditingIndex] = useState(null);
    const [editingSlotId, setEditingSlotId] = useState(null);

    // State for toast notification
    const [hideToast, setHideToast] = useState(true);

    // Validation states
    const [isSessionNameValid, setIsSessionNameValid] = useState(true);
    const [isQuarterValid, setIsQuarterValid] = useState(true);
    const [isNoOfSessionsValid, setIsNoOfSessionsValid] = useState(true);
    const [isCreateButtonEnabled, setIsCreateButtonEnabled] = useState(false);
    const [isAddButtonDisabled, setIsAddButtonDisabled] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingRegisteredCount, setEditingRegisteredCount] = useState(0);


    const empId = encryptStorage.getItem('employeeId')
    // Error messages
    const [errorMessages, setErrorMessages] = useState({
        sessionName: '',
        quarter: '',
        noOfSessions: '',
        sessionDate: '',
        startTime: '',
        endTime: '',
        registrationsAllowed: ''
    });

    // Effect to check if Create button should be enabled
    useEffect(() => {
        if (sessionName && quarter && noOfSessions && sessions.length === parseInt(noOfSessions)) {
            setIsCreateButtonEnabled(true);
        } else {
            setIsCreateButtonEnabled(false);
        }
    }, [sessionName, quarter, noOfSessions, sessions]);

    // Effect to check if Add button should be enabled
    useEffect(() => {
        const isAllFieldsFilled = sessionDate && startTime && endTime && registrationsAllowed;
        const isSlotCountLessThanMax = sessions.length < parseInt(noOfSessions);
        setIsAddButtonDisabled(!isAllFieldsFilled || !isSlotCountLessThanMax);
    }, [sessionDate, startTime, endTime, registrationsAllowed, sessions, noOfSessions]);

    // Effect to prefill data if editing
    useEffect(() => {
        if (sessionData) {
            setSessionName(sessionData.session_name || '');
            setQuarter(sessionData.quarter || '');
            setNoOfSessions(sessionData.session_count || '');
            setGrade(sessionData.grade || '')
            // Prefill slots (table data)
            setSessions(
                (sessionData.slots || []).map(slot => ({
                    slot_id: slot.slot_id,
                    sessionDate: slot.session_date,
                    startTime: slot.start_time,
                    endTime: slot.end_time,
                    registrationsAllowed: slot.seat_available,
                    registeredCount: slot.registered_count
                }))
            );
            resetInlineFields(); // Always clear the inline add row!
        } else {
            setSessionName('');
            setQuarter('');
            setNoOfSessions('');
            setSessions([]);
            resetInlineFields();
        }
    }, [sessionData]);

    // Function to validate session name
    const validateSessionName = (name) => {
        if (!name) {
            setErrorMessages(prev => ({ ...prev, sessionName: 'Please enter the session name' }));
            return false;
        }
        if (name.length > 50) {
            setErrorMessages(prev => ({ ...prev, sessionName: 'Session cannot exceed 50 characters' }));
            return false;
        }

        // Regex to validate session name
        const isValid = /^[a-zA-Z0-9 ]+$/.test(name) &&
            !/^[0-9]+$/.test(name) &&
            !/^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/.test(name) &&
            !/^\s*$/.test(name);

        if (!isValid) {
            setErrorMessages(prev => ({ ...prev, sessionName: 'Enter a valid session name' }));
        } else {
            setErrorMessages(prev => ({ ...prev, sessionName: '' }));
        }
        return isValid;
    };


    // Function to handle adding or updating a session
    const handleAddSession = () => {
        // Validate inline fields
        let valid = true;
        let newErrorMessages = { ...errorMessages };

        if (!sessionDate) {
            newErrorMessages.sessionDate = 'Enter session date';
            valid = false;
        } else {
            newErrorMessages.sessionDate = '';
        }

        if (!startTime) {
            newErrorMessages.startTime = 'Enter start time';
            valid = false;
        } else {
            newErrorMessages.startTime = '';
        }

        if (!endTime) {
            newErrorMessages.endTime = 'Enter end time';
            valid = false;
        } else if (endTime <= startTime) {
            newErrorMessages.endTime = 'End time must be earlier than Start time';
            valid = false;
        } else {
            newErrorMessages.endTime = '';
        }

        if (!registrationsAllowed) {
            newErrorMessages.registrationsAllowed = 'Enter registrations allowed count';
            valid = false;
        } else if (parseInt(registrationsAllowed) < 1) { // Change this line
            newErrorMessages.registrationsAllowed = 'Registration allowed must be at least 1'; // Update error message
            valid = false;
        } else {
            newErrorMessages.registrationsAllowed = '';
        }

        if (isEditing && editingRegisteredCount > 0 && parseInt(registrationsAllowed) < editingRegisteredCount) {
            newErrorMessages.registrationsAllowed = `Cannot reduce seats below registered count (${editingRegisteredCount})`;
            valid = false;
        }


        setErrorMessages(newErrorMessages);

        if (valid && validateSessionName(sessionName)) {
            const newSession = { sessionDate, startTime, endTime, registrationsAllowed };
            if (editingIndex !== null) {
                // Update existing session
                if (editingSlotId) {
                    newSession.slot_id = editingSlotId;
                }
                const updatedSessions = [...sessions];
                updatedSessions.splice(editingIndex, 0, newSession);
                setSessions(updatedSessions);
                setEditingIndex(null); // Reset editing index
                setEditingSlotId(null);
            } else {
                // Add new session
                newSession.slot_id = "";
                setSessions([...sessions, newSession]);
            }
            // Reset inline fields after adding/updating
            resetInlineFields();
            setIsEditing(false);
        }
    };


    // Function to reset inline fields
    const resetInlineFields = () => {
        setSessionDate('');
        setStartTime('');
        setEndTime('');
        setRegistrationsAllowed('');
        setEditingRegisteredCount(0);
    };

    // Function to sort sessions based on a key
    const sortSessions = (key) => {
        const newSortDirection = sortedColumn === key && sortDirection === 'asc' ? 'desc' : 'asc';
        const sortedSessions = [...sessions].sort((a, b) => {
            if (newSortDirection === 'asc') {
                if (key === 'sessionDate') return new Date(a.sessionDate) - new Date(b.sessionDate);
                if (key === 'startTime') return a.startTime.localeCompare(b.startTime);
                if (key === 'endTime') return a.endTime.localeCompare(b.endTime);
                if (key === 'registrationsAllowed') return a.registrationsAllowed - b.registrationsAllowed;
            } else {
                if (key === 'sessionDate') return new Date(b.sessionDate) - new Date(a.sessionDate);
                if (key === 'startTime') return b.startTime.localeCompare(a.startTime);
                if (key === 'endTime') return b.endTime.localeCompare(a.endTime);
                if (key === 'registrationsAllowed') return b.registrationsAllowed - a.registrationsAllowed;
            }
            return 0;
        });
        setSessions(sortedSessions);
        setSortedColumn(key);
        setSortDirection(newSortDirection);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(Date.UTC(year, month - 1, day)); // Treat as UTC
        const options = { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' };
        return date.toLocaleDateString('en-US', options).replace(/\//g, '-');
    };


    // Function to handle editing a session
    const handleEditSession = (index) => {
        // Remove the session from the list before editing
        const sessionToEdit = sessions[index];

        // Remove the selected session from the list
        const updatedSessions = sessions.filter((_, i) => i !== index);

        // Set the state with updated sessions and pre-fill the form fields
        setSessions(updatedSessions);

        setSessionDate(sessionToEdit.sessionDate.split('T')[0]);
        setStartTime(ampmTo24(sessionToEdit.startTime));
        setEndTime(ampmTo24(sessionToEdit.endTime));
        setRegistrationsAllowed(sessionToEdit.registrationsAllowed);
        setEditingRegisteredCount(sessionToEdit.registeredCount || 0);
        // Set editing index to null as we are removing it from the list
        setEditingIndex(index);
        setEditingSlotId(sessionToEdit.slot_id || null);
        setIsEditing(true);
    };

    // Function to handle deleting a session
    const handleDeleteSession = (index) => {
        const sessionToDelete = sessions[index];
        // If the slot exists in backend, track it for deletion
        if (sessionToDelete.slot_id) {
            setDeletedSlots(prev => [
                ...prev,
                { ...sessionToDelete, status: "Delete" }
            ]);
        }
        // Remove from sessions array
        const updatedSessions = sessions.filter((_, i) => i !== index);
        setSessions(updatedSessions);
    };

    const handleGradeChange = (e) => {
        setGrade(e.target.value);
    };

    // Seq - 1.49 to 1.52
    // The date range picker is shown based on the selected quarter.
    // Helper Function to Calculate Quarter Date Range
    // Function to get quarter date range
    const getQuarterDateRange = (quarter, year = new Date().getFullYear()) => {
        switch (quarter) {
            case '1':
                return { min: `${year}-04-01`, max: `${year}-06-30` }; // April to June
            case '2':
                return { min: `${year}-07-01`, max: `${year}-09-30` }; // July to September
            case '3':
                return { min: `${year}-10-01`, max: `${year}-12-31` }; // October to December
            case '4':
                return { min: `${year + 1}-01-01`, max: `${year + 1}-03-31` }; // January to March
            default:
                return { min: getTodayDate(), max: getTodayDate() };
        }
    };

    // Function to handle creating a session
    const handleCreateSession = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        // Prepare active (non-deleted) slots
        const activeSlots = sessions.map((session) => ({
            slot_id: session.slot_id || "",
            session_date: session.sessionDate,
            start_time: session.startTime,
            end_time: session.endTime,
            seat_available: parseInt(session.registrationsAllowed),
            status: "Active", // or omit if not needed
        }));

        // Prepare deleted slots (already have status: "Delete")
        const deleted = deletedSlots.map(slot => ({
            slot_id: slot.slot_id,
            session_date: slot.sessionDate,
            start_time: slot.startTime,
            end_time: slot.endTime,
            seat_available: parseInt(slot.registrationsAllowed),
            status: "Delete",
        }));

        const payload = {
            session_id: sessionData ? sessionData.session_id : "",
            quarter: quarter,
            session_name: sessionName,
            session_count: activeSlots.length,
            grade: grade,
            session_details: [...activeSlots, ...deleted],
            empId: empId
        };

        try {
            const response = await PostSessions(payload);
            if (response.data.code === 201) {
                Toast.success('Session Created Successfully !')
                resetForm();
                fetchSessions();
                onClose();
            } else if (response.data.code === 200) {
                Toast.success('Session Updated Successfully !')
                resetForm();
                fetchSessions();
                onClose();
            }
        } catch (error) {
            console.error("Error creating session:", error);
            Toast.error('Failed to create session.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Function to reset form fields after successful submission
    const resetForm = () => {
        setSessionName('');
        setQuarter('');
        setNoOfSessions('');
        setSessions([]);
        setDeletedSlots([]);
        resetInlineFields();
    };

    const handleClose = () => {
        onClose(); // Call the onClose function passed as a prop
    };

    // AM/PM to 24-hour conversion for input fields
    const ampmTo24 = (timeStr) => {
        if (!timeStr) return '';
        const [time, modifier] = timeStr.split(' ');
        if (!modifier) return timeStr; // Already 24-hour format
        let [hours, minutes] = time.split(':');
        hours = parseInt(hours, 10);
        if (modifier.toUpperCase() === 'PM' && hours !== 12) hours += 12;
        if (modifier.toUpperCase() === 'AM' && hours === 12) hours = 0;
        return `${hours.toString().padStart(2, '0')}:${minutes}`;
    };

    const convertTo12HourFormat = (timeStr) => {
        if (!timeStr) return '';

        // Check if the time string contains AM or PM
        if (timeStr.includes('AM') || timeStr.includes('PM')) {
            return timeStr; // Return as is if it's already in 12-hour format
        }

        const [hours, minutes] = timeStr.split(':');
        const hour = parseInt(hours, 10);
        const isPM = hour >= 12;
        const formattedHour = hour % 12 || 12; // Convert 0 to 12 for midnight
        const formattedTime = `${formattedHour}:${minutes} ${isPM ? 'PM' : 'AM'}`;
        return formattedTime;
    };

    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1" aria-labelledby="staticBackdropLabel" aria-hidden="true">
            <div className="modal-dialog modal-lg sessions-modal modal-dialog-scrollable">
                <div className="modal-content modal-content-dynamic">
                    <div className="modal-header">
                        <h5 className="modal-title font-14" id="staticBackdropLabel">Create Session</h5>
                        <button type="button" className="btn-close" onClick={handleClose} aria-label="Close"></button>
                    </div>

                    <div className="modal-body">
                        <div className="row">
                            <div className="col-md-3 mb-3">
                                <label className="font-12 mb-1 regular-text grey-text d-block" htmlFor="sessionName">
                                    Session Name
                                    <span className="text-danger font-12">*</span>
                                </label>
                                <input type="text" className={`form-control hive-form-control font-12 ${!isSessionNameValid ? 'is-invalid' : ''}`}
                                    id="sessionName"
                                    placeholder="Enter Session Name"
                                    value={sessionName}
                                    onChange={(e) => {
                                        setSessionName(e.target.value);
                                        validateSessionName(e.target.value);
                                    }} />
                                {errorMessages.sessionName && <span className="text-danger font-12 font-12">{errorMessages.sessionName}</span>}
                            </div>
                            <div class="col-md-3 mb-3">
                                <label class="font-12 mb-1 regular-text grey-text d-block" for="grade">
                                    Grade
                                    <span class="text-danger">*</span>
                                </label>
                                <select class="form-select font-12" id="grade" value={grade} onChange={handleGradeChange} disabled={sessions.length > 0} aria-label="Default select example">
                                    <option selected hidden>Select</option>
                                    <option value="A">A</option>
                                    <option value="B">B</option>
                                    <option value="C">C</option>
                                    <option value="A,C">A & C</option>
                                    <option value="A,B">A & B</option>
                                    <option value="B,C">B & C</option>
                                    <option value="F">F</option>
                                </select>
                            </div>

                            <div className="col-md-3 mb-3">
                                <label className="font-12 mb-1 regular-text grey-text d-block" htmlFor="quarter">
                                    Quarter
                                    <span className="text-danger font-12">*</span>
                                </label>
                                <select className={`form-select font-12 ${!isQuarterValid ? 'is-invalid' : ''}`} id="quarter" disabled={sessions.length > 0} value={quarter} onChange={(e) => {
                                    setQuarter(e.target.value);
                                    setSessionDate('');
                                    setIsQuarterValid(e.target.value !== '');
                                    if (!e.target.value) {
                                        setErrorMessages(prev => ({ ...prev, quarter: 'Please Select the quarter' }));
                                    } else {
                                        setErrorMessages(prev => ({ ...prev, quarter: '' }));
                                    }
                                }}>
                                    <option value="">Select</option>
                                    <option value="1">Quarter I</option>
                                    <option value="2">Quarter II</option>
                                    <option value="3">Quarter III</option>
                                    <option value="4">Quarter IV</option>
                                </select>
                                {errorMessages.quarter && <span className="text-danger font-12">{errorMessages.quarter}</span>}
                            </div>

                            <div className="col-md-3 mb-3">
                                <label className="font-12 mb-1 regular-text grey-text d-block" htmlFor="noOfSessions">
                                    No. of Sessions
                                    <span className="text-danger font-12">*</span>
                                </label>
                                <input type="text" className={`form-control hive-form-control font-12 ${!isNoOfSessionsValid ? 'is-invalid' : ''}`}
                                    id="noOfSessions"
                                    placeholder="Enter"
                                    value={noOfSessions}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (/^\d{0,3}$/.test(value)) {
                                            setNoOfSessions(value);
                                            setIsNoOfSessionsValid(true);
                                        } else {
                                            setIsNoOfSessionsValid(false);
                                            setErrorMessages(prev => ({ ...prev, noOfSessions: 'Please enter a valid number' }));
                                        }
                                        if (!value) {
                                            setErrorMessages(prev => ({ ...prev, noOfSessions: 'Please Enter the session count' }));
                                        } else {
                                            setErrorMessages(prev => ({ ...prev, noOfSessions: '' }));
                                        }
                                    }} />
                                {errorMessages.noOfSessions && <span className="text-danger font-12">{errorMessages.noOfSessions}</span>}
                            </div>

                            <div className="col-md-12 mt-3">
                                <table className="table bg-white hive-grid mb-3 table-borderless session-inline-table">
                                    <thead className="hive-table-head">
                                        <tr>
                                            <th className="bold-text" onClick={() => sortSessions('sessionDate')}>
                                                Session Date
                                                <span className="ps-2">
                                                    <img src={sortedColumn === 'sessionDate' && sortDirection === 'asc' ? "/Images/Sort-up.svg" : "/Images/sort-icon.svg"} alt="Sorticon" className="hive-table-sort" />
                                                </span>
                                            </th>
                                            <th className="bold-text" onClick={() => sortSessions('startTime')}>
                                                Start Time
                                                <span className="ps-2">
                                                    <img src={sortedColumn === 'startTime' && sortDirection === 'asc' ? "/Images/Sort-up.svg" : "/Images/sort-icon.svg"} alt="Sorticon" className="hive-table-sort" />
                                                </span>
                                            </th>
                                            <th className="bold-text" onClick={() => sortSessions('endTime')}>
                                                End Time
                                                <span className="ps-2">
                                                    <img src={sortedColumn === 'endTime' && sortDirection === 'asc' ? "/Images/Sort-up.svg" : "/Images/sort-icon.svg"} alt="Sorticon" className="hive-table-sort" />
                                                </span>
                                            </th>
                                            <th className="bold-text" onClick={() => sortSessions('registrationsAllowed')}>
                                                Registrations Allowed
                                                <span className="ps-2">
                                                    <img src={sortedColumn === 'registrationsAllowed' && sortDirection === 'asc' ? "/Images/Sort-up.svg" : "/Images/sort-icon.svg"} alt="Sorticon" className="hive-table-sort" />
                                                </span>
                                            </th>
                                            <th className="text-center bold-text">Actions</th>
                                        </tr>
                                    </thead>

                                    <tbody className="overflow-auto">
                                        {/* Inline row for adding a new session */}
                                        <tr className="hive-table-data regular-text align-middle">
                                            <td>
                                                <input
                                                    type="date"
                                                    className={`form-control hive-form-control font-12 ${errorMessages.sessionDate ? 'is-invalid' : ''}`}
                                                    value={sessionDate}
                                                    min={quarter ? getQuarterDateRange(quarter).min : getTodayDate()}
                                                    max={quarter ? getQuarterDateRange(quarter).max : undefined}
                                                    onKeyDown={(e) => {
                                                        e.preventDefault();
                                                    }}
                                                    onChange={(e) => {
                                                        setSessionDate(e.target.value);
                                                        if (!e.target.value) {
                                                            setErrorMessages(prev => ({ ...prev, sessionDate: 'Please enter the session date' }));
                                                        } else {
                                                            setErrorMessages(prev => ({ ...prev, sessionDate: '' }));
                                                        }
                                                    }}
                                                />
                                                {errorMessages.sessionDate && <span className="text-danger font-12">{errorMessages.sessionDate}</span>}
                                            </td>
                                            <td>
                                                <input type="time"
                                                    className={`form-control hive-form-control font-12 ${errorMessages.startTime ? 'is-invalid' : ''}`}
                                                    value={startTime}
                                                    onKeyDown={(e) => {
                                                        e.preventDefault();
                                                    }}
                                                    onChange={(e) => {
                                                        setStartTime(e.target.value);
                                                        if (!e.target.value) {
                                                            setErrorMessages(prev => ({ ...prev, startTime: 'Please enter the start time' }));
                                                        } else {
                                                            setErrorMessages(prev => ({ ...prev, startTime: '' }));
                                                        }
                                                    }} />
                                                {errorMessages.startTime && <span className="text-danger font-12">{errorMessages.startTime}</span>}
                                            </td>
                                            <td>
                                                <input type="time"
                                                    className={`form-control hive-form-control font-12 ${errorMessages.endTime ? 'is-invalid' : ''}`}
                                                    value={endTime}
                                                    onKeyDown={(e) => {
                                                        e.preventDefault();
                                                    }}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (value <= startTime) {
                                                            setErrorMessages(prev => ({ ...prev, endTime: 'End time must be earlier than Start time' }));
                                                        } else {
                                                            setErrorMessages(prev => ({ ...prev, endTime: '' }));
                                                        }
                                                        setEndTime(value);
                                                    }} />
                                                {errorMessages.endTime && <span className="text-danger font-12">{errorMessages.endTime}</span>}
                                            </td>
                                            <td>
                                                <input type="text"
                                                    className={`form-control hive-form-control font-12 ${errorMessages.registrationsAllowed ? 'is-invalid' : ''}`}
                                                    placeholder="Enter"
                                                    value={registrationsAllowed}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (/^\d{0,3}$/.test(value)) {
                                                            setRegistrationsAllowed(value);

                                                            // Only run this check if editing an existing slot
                                                            if (isEditing && editingRegisteredCount > 0) {
                                                                if (parseInt(value) < editingRegisteredCount) {
                                                                    setErrorMessages(prev => ({
                                                                        ...prev,
                                                                        registrationsAllowed: `Cannot be less than already registered count (${editingRegisteredCount})`
                                                                    }));
                                                                } else {
                                                                    setErrorMessages(prev => ({ ...prev, registrationsAllowed: '' }));
                                                                }
                                                            } else {
                                                                // Your existing validation
                                                                if (!value) {
                                                                    setErrorMessages(prev => ({ ...prev, registrationsAllowed: 'Please enter registrations allowed count' }));
                                                                } else if (parseInt(value) < 1) {
                                                                    setErrorMessages(prev => ({ ...prev, registrationsAllowed: 'Registration allowed must be at least 1' }));
                                                                } else {
                                                                    setErrorMessages(prev => ({ ...prev, registrationsAllowed: '' }));
                                                                }
                                                            }
                                                        } else {
                                                            setErrorMessages(prev => ({ ...prev, registrationsAllowed: 'Please enter a valid number' }));
                                                        }
                                                    }}
                                                />
                                                {errorMessages.registrationsAllowed && <span className="text-danger font-12">{errorMessages.registrationsAllowed}</span>}
                                            </td>
                                            <td className="text-center">
                                                <button
                                                    className={`bg-transparent border-0 pe-0 ${isAddButtonDisabled ? 'disabled' : ''}`}
                                                    onClick={handleAddSession}
                                                    disabled={isAddButtonDisabled}>
                                                    <img src={isAddButtonDisabled ? "/Images/inline-disable-add.svg" : "/Images/inline-add-icon.svg"} alt={isAddButtonDisabled ? "disabled add" : "add"} />
                                                </button>
                                            </td>
                                        </tr>

                                        {/* Display added sessions */}
                                        {sessions.length > 0 ? sessions.map((session, index) => (
                                            <tr key={index} className="hive-table-data regular-text align-middle">
                                                <td>{formatDate(session.sessionDate)}</td>
                                                <td>{convertTo12HourFormat(session.startTime)}</td>
                                                <td>{convertTo12HourFormat(session.endTime)}</td>
                                                <td className="text-end">{session.registrationsAllowed}</td>
                                                <td className="text-center">
                                                    {/* Edit and delete buttons */}
                                                    <button
                                                        className="bg-transparent border-0 pe-0"
                                                        onClick={() => handleEditSession(index)}
                                                        disabled={isEditing}>
                                                        <img src="/Images/edit-action.svg" alt="edit" />
                                                    </button>
                                                    <button
                                                        className="bg-transparent border-0"
                                                        onClick={() => handleDeleteSession(index)}
                                                        disabled={isEditing}>
                                                        <img src="/Images/delete-action.svg" alt="delete" />
                                                    </button>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr className="hive-table-data regular-text align-middle">
                                                <td colSpan={5} className="vh-25">
                                                    <div className="d-flex flex-column align-items-center justify-content-center">
                                                        <img src="/Images/table-empty-state.svg" alt="empty-state" />
                                                        <p className="font-9 grey-text mt-2">Nothing to show</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer border-0">
                        <div className="col-md-12 d-flex justify-content-end">
                            <button className="btn hive-btn-secondary me-3" onClick={handleClose}>Cancel</button>
                            <button
                                className={`btn hive-btn-primary ${(!isCreateButtonEnabled || isSubmitting) ? 'disabled' : ''}`}
                                disabled={!isCreateButtonEnabled || isSubmitting}
                                onClick={handleCreateSession}
                            >
                                {isSubmitting ? (
                                    <span>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        {sessionData ? 'Updating...' : 'Creating...'}
                                    </span>
                                ) : (
                                    sessionData ? 'Update Session' : 'Create Session'
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Toast Notification */}
                    <div
                        className="position-fixed col-md-12 mt-3 position-toast d-flex justify-content-center end-0 p-3"
                        style={{ zIndex: 11 }}
                    >
                        <div
                            id="liveToast"
                            className="toast side-toast show"
                            role="alert"
                            aria-live="assertive"
                            aria-atomic="true"
                            hidden={hideToast}
                        >
                            <div className="col-md-12 p-2 float-start">
                                <div className>
                                    <div className="toast-header border-toast">
                                        <img className="tick px-2 float-start" src="/Images/green-tick.svg" alt="toast" />
                                        <div className="float-start px-3 pe-3">
                                            <div className="bold-text font-18 toast-font-black">Session Registered Successfully</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};